import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { CollectedAt, Pill, SeverityBadge, label } from './components/primitives'
import { acknowledgeAlert, closeAlert, listAlerts, type Alert } from './api'

/**
 * What the watches found.
 *
 * ## Closing asks WHICH ending
 *
 * `ACTIONED` and `DISMISSED` are offered as separate buttons rather than one "close", because
 * "somebody looked into this" and "somebody decided it was nothing" are the two facts a
 * review will want to tell apart — and a single CLOSED state loses the difference forever.
 */
export function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [statusCode, setStatusCode] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setAlerts(await listAlerts({ statusCode: statusCode || undefined }))
    } catch (err) {
      setError(explain(err))
    }
  }, [statusCode])

  useEffect(() => { void load() }, [load])

  async function act(alert: Alert, action: 'ack' | 'ACTIONED' | 'DISMISSED') {
    setBusy(alert.id)
    try {
      if (action === 'ack') await acknowledgeAlert(alert.id)
      else await closeAlert(alert.id, action)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (error && !alerts) return <ErrorStrip message={error} />
  if (!alerts) return <LoadingBlock label="Loading alerts" />

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Alerts"
        description="One firing of one watch. Scope-filtered — you see what fired in your own
                     area."
      />
      {error && <ErrorStrip message={error} />}
      <Panel>
        <div className="flex items-end gap-2 p-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Status
            </span>
            <select value={statusCode} onChange={(e) => setStatusCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              <option value="">All</option>
              {['NEW', 'ACKNOWLEDGED', 'ACTIONED', 'DISMISSED', 'EXPIRED'].map((s) => (
                <option key={s} value={s}>{label(s)}</option>
              ))}
            </select>
          </label>
        </div>
      </Panel>
      <Panel>
        <ErpTable head={<>
          <Th>Severity</Th>
          <Th>Alert</Th>
          <Th>Status</Th>
          <Th>Fired</Th>
          <Th>Decide</Th>
        </>}>
          {alerts.length === 0 && (
            <EmptyRow colSpan={5}
                      message="Nothing has fired. A watch with no alerts is a watch nobody has
                               had to act on — which is the intended state." />
          )}
          {alerts.map((alert) => (
            <tr key={alert.id} className="hover:bg-muted/40">
              <Td><SeverityBadge severity={alert.severity} /></Td>
              <Td>
                <span className="font-medium">{alert.title}</span>
                {alert.detail && (
                  <span className="block text-[10px] text-muted-foreground">{alert.detail}</span>
                )}
              </Td>
              <Td><Pill className="border-border">{label(alert.statusCode)}</Pill></Td>
              <Td><CollectedAt at={alert.firedAt} /></Td>
              <Td>
                <div className="flex gap-1">
                  {alert.statusCode === 'NEW' && (
                    <button type="button" disabled={busy === alert.id}
                            onClick={() => void act(alert, 'ack')}
                            className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                      Acknowledge
                    </button>
                  )}
                  {(alert.statusCode === 'NEW' || alert.statusCode === 'ACKNOWLEDGED') && (
                    <>
                      <button type="button" disabled={busy === alert.id}
                              onClick={() => void act(alert, 'ACTIONED')}
                              className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                        Actioned
                      </button>
                      <button type="button" disabled={busy === alert.id}
                              onClick={() => void act(alert, 'DISMISSED')}
                              className="rounded border border-border px-1.5 py-0.5 text-[11px]">
                        Nothing in it
                      </button>
                    </>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
